import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  Input,
  AfterViewInit,
} from '@angular/core';
import { IonInput } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  PlatformEnum,
  PlatformService,
  PanAppState,
  jwtDecode,
} from '@panamax/app-state';
import { OrderHeader, OrderItem } from '@usf/ngrx-order';
import { Subscription } from 'rxjs';
import { OrderViewModel } from '../../../models/order-view-model';
import { OrderService } from '../../../services/order.service';
import { UserService } from '@app/user/services';
import { UserPreferencesV2 } from '@usf/user-types/user-preferencev2';
import { FEATURES } from '@shared/constants/splitio-features';
import { take, filter, switchMap, tap } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { PLATFORM_CAPACITOR_TYPE } from '@shared/constants/platform-capacitor';

@Component({
  selector: 'app-change-po',
  templateUrl: './change-po.component.html',
  styleUrls: ['./change-po.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePoComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() platform: PlatformEnum;
  @Output() poUpdated = new EventEmitter();
  @Output() titleIdentified = new EventEmitter<string>();

  @ViewChild('poNumberInput', { static: false }) poNumberInput: IonInput;

  hasPoNumber: boolean;
  poNumber: string;
  poRegex = new RegExp('^[-0-9a-zA-Z ]{1,20}$');
  orderViewModelSub$: Subscription = new Subscription();
  featureFlagSub$: Subscription;
  defaultPoSub$: Subscription;
  vm: OrderViewModel;
  saveDefaultPo = false;
  defaultPoFeatureFlag = FEATURES.split_dynamic_default_po.name;
  isIos = false;
  isPoDisabledForVPTAndPunchOut = false;
  customerNumber: number;

  constructor(
    private orderService: OrderService,
    readonly platformService: PlatformService,
    private translateService: TranslateService,
    private userService: UserService,
    readonly panAppState: PanAppState,
  ) {
    this.isIos = Capacitor.getPlatform() === PLATFORM_CAPACITOR_TYPE.IOS;
  }

  // Dialogs are initialized and destroyed as normal angular app, no memory leaks
  ngOnInit() {
    this.panAppState.customer$
      .pipe(
        take(1),
        filter(customer => !!customer),
      )
      .subscribe(customer => {
        this.customerNumber = customer.customerNumber;
      });

    this.orderViewModelSub$ = this.orderService
      .loadOrderViewModel$()
      .subscribe((viewModel: OrderViewModel) => {
        this.vm = viewModel;
        this.poNumber = this.vm.orderHeader.purchaseOrderNumber;
        this.hasPoNumber = this.poNumber !== undefined;
        this.titleIdentified.emit(
          this.translateService.instant('i18n.orderPage.editPoText'),
        );
      });
    this.validatePoForVPTAndPunchOutUser();
  }

  validatePoForVPTAndPunchOutUser() {
    this.panAppState.accessToken$
      .pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          this.isPoDisabledForVPTAndPunchOut =
            decoded['usf-claims']?.punchoutSession ||
            decoded['usf-claims']?.punchthruSession;
        }
      });
  }

  ngOnDestroy() {
    if (this.orderViewModelSub$) {
      this.orderViewModelSub$.unsubscribe();
    }
    if (this.featureFlagSub$) {
      this.featureFlagSub$.unsubscribe();
    }
    if (this.defaultPoSub$) {
      this.defaultPoSub$.unsubscribe();
    }
  }

  ngAfterViewInit() {
    if (this.poNumberInput) {
      // Delay focus for iOS to prevent keyboard flicker issues
      if (this.isIos) {
        setTimeout(() => {
          this.poNumberInput?.setFocus();
        }, 300);
      } else {
        setTimeout(() => {
          this.poNumberInput?.setFocus();
        }, 150);
      }
    }
  }

  async onUpdate() {
    if (this.saveDefaultPo) {
      const currentPreferences = await this.userService
        .getUserPreferencesV2()
        .pipe(take(1))
        .toPromise();

      const updatedPreferences: UserPreferencesV2 = {
        ...currentPreferences,
        poNumber: [
          ...(Array.isArray(currentPreferences.poNumber)
            ? currentPreferences.poNumber
            : []
          ).filter(po => po.customerNumber !== this.customerNumber),
          {
            customerNumber: this.customerNumber,
            defaultPo: this.poNumber,
          },
        ],
      };

      await this.userService
        .updateUserPreferencesV2(updatedPreferences)
        .toPromise();
    }

    this.updatePoNumber();
    this.poUpdated.emit();
  }

  updatePoNumber() {
    const newOrder: OrderHeader = {
      ...this.vm.orderHeader,
      purchaseOrderNumber: this.poNumber,
      dirtyFlag: true,
    };
    const orderItems: OrderItem[] = [];
    for (const key in this.vm.orderItemState.entities) {
      if (
        Object.prototype.hasOwnProperty.call(
          this.vm.orderItemState.entities,
          key,
        )
      ) {
        const element = this.vm.orderItemState.entities[key];
        orderItems.push(element);
      }
    }
    this.orderService.updateOrderHeader(newOrder, orderItems);
  }
}
